/**
 * TemplateMenu — chart template save / load / delete dropdown.
 *
 * A template captures the current pane's chart type and all active
 * indicators (with their parameters and visibility) into a named preset
 * stored in localStorage.
 *
 * Features:
 * - List saved templates with one-click apply
 * - Set a template as the default (auto-applied to new panels)
 * - Save current state as a new named template
 * - Delete individual templates
 */
import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { ChartPanelHandle } from "./ChartPanel";
import { ChartTemplate, ChartTemplateIndicator, loadTemplates } from "@service/local-storage-handler";
import { syncSaveTemplate, syncDeleteTemplate, syncSetDefaultTemplate } from "@service/sync-handler";
import { ChartPanel as ChartPanelType } from "../../types/chart.types";
import "./TemplateMenu.scss";

interface TemplateMenuProps {
  activePanelRef: React.RefObject<ChartPanelHandle | null>;
  activePanelId: string | null;
  colorTheme: string;
  compact?: boolean;
}

const TemplateMenu: React.FC<TemplateMenuProps> = ({ activePanelRef, activePanelId, colorTheme, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [templates, setTemplates] = useState<ChartTemplate[]>(() => loadTemplates());
  const [isSaving, setIsSaving] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [overwriteTarget, setOverwriteTarget] = useState<ChartTemplate | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const saveInputRef = useRef<HTMLInputElement>(null);

  // Read current chart type from Redux
  const activePanel = useSelector((state: any) =>
    (state.chart?.panels || []).find((p: ChartPanelType) => p.id === activePanelId) ?? null
  );

  // Close dropdown on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current && !dropdownRef.current.contains(target) &&
        triggerRef.current && !triggerRef.current.contains(target)
      ) {
        setIsOpen(false);
        setIsSaving(false);
        setSaveName("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Refresh templates list whenever dropdown opens
  useEffect(() => {
    if (isOpen) {
      setTemplates(loadTemplates());
    }
  }, [isOpen]);

  // Auto-focus the save input when it appears
  useEffect(() => {
    if (isSaving && saveInputRef.current) {
      saveInputRef.current.focus();
    }
  }, [isSaving]);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => {
      if (prev) {
        setIsSaving(false);
        setSaveName("");
        return false;
      }
      const rect = triggerRef.current?.getBoundingClientRect() ?? null;
      setAnchorRect(rect);
      return true;
    });
  }, []);

  const buildTemplateFromCurrent = useCallback((name: string): ChartTemplate => {
    const handle = activePanelRef.current;
    const indicators: ChartTemplateIndicator[] = handle
      ? handle.getActiveIndicators().map(a => ({
          indicatorId: a.indicatorId,
          params: a.params,
          visible: a.visible,
        }))
      : [];

    return {
      id: `tpl_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      name,
      isDefault: false,
      chartType: activePanel?.chartType ?? "candlestick",
      indicators,
      visibleBarsCount: handle ? handle.getVisibleBarsCount() : undefined,
      createdAt: Date.now(),
    };
  }, [activePanelRef, activePanel]);

  const handleSaveTemplate = useCallback(() => {
    const name = saveName.trim();
    if (!name) return;

    // Check if a template with this name already exists
    const existing = templates.find(t => t.name.toLowerCase() === name.toLowerCase());
    if (existing) {
      setOverwriteTarget(existing);
      return;
    }

    const template = buildTemplateFromCurrent(name);
    syncSaveTemplate(template);
    setTemplates(loadTemplates());
    setSaveName("");
    setIsSaving(false);
  }, [saveName, templates, buildTemplateFromCurrent]);

  const handleOverwrite = useCallback(() => {
    if (!overwriteTarget) return;
    const template = buildTemplateFromCurrent(overwriteTarget.name);
    // Reuse the existing id and preserve default status
    template.id = overwriteTarget.id;
    template.isDefault = overwriteTarget.isDefault;
    syncSaveTemplate(template);
    setTemplates(loadTemplates());
    setSaveName("");
    setIsSaving(false);
    setOverwriteTarget(null);
  }, [overwriteTarget, buildTemplateFromCurrent]);

  const handleRename = useCallback(() => {
    // Dismiss the dialog and re-focus the input so the user can pick a new name
    setOverwriteTarget(null);
    if (saveInputRef.current) {
      saveInputRef.current.focus();
      saveInputRef.current.select();
    }
  }, []);

  const handleApply = useCallback((template: ChartTemplate) => {
    const handle = activePanelRef.current;
    if (handle) {
      handle.applyTemplate(template);
    }
    setIsOpen(false);
  }, [activePanelRef]);

  const handleDelete = useCallback((id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    syncDeleteTemplate(id);
    setTemplates(loadTemplates());
  }, []);

  const handleToggleDefault = useCallback((id: string, currentlyDefault: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    syncSetDefaultTemplate(currentlyDefault ? null : id);
    setTemplates(loadTemplates());
  }, []);

  return (
    <div className={`template-menu ${colorTheme}`}>
      <button
        ref={triggerRef}
        className="toolbar-btn template-trigger-btn"
        onClick={handleToggle}
        title="Chart Templates"
      >
        {/* Template / layout icon */}
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="9" y1="9" x2="9" y2="21" />
        </svg>
        <span className="template-label">Templates</span>
        <span className="dropdown-arrow">▼</span>
      </button>

      {isOpen && anchorRect && createPortal(
        <div ref={dropdownRef} className="toolbar-dropdown template-dropdown" style={{
          position: "fixed",
          top: anchorRect.bottom + 2,
          left: anchorRect.left,
          zIndex: 99999,
        }}>
          <div className="template-list-header">
            <span>Saved Templates</span>
            <button className="template-close-btn" onClick={() => setIsOpen(false)} title="Close">
              <svg viewBox="0 0 20 20" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="4" y1="4" x2="16" y2="16" />
                <line x1="16" y1="4" x2="4" y2="16" />
              </svg>
            </button>
          </div>

          {templates.length === 0 ? (
            <div className="template-empty">No saved templates yet</div>
          ) : (
            templates.map(tpl => (
              <div
                key={tpl.id}
                className="template-item"
                onClick={() => handleApply(tpl)}
                title={`Apply "${tpl.name}"`}
              >
                <span className="template-item-name">
                  {tpl.isDefault && (
                    <svg className="template-default-icon" viewBox="0 0 24 24" width="11" height="11" fill="currentColor" stroke="none">
                      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                    </svg>
                  )}
                  {tpl.name}
                </span>
                <div className="template-item-actions">
                  <button
                    className={`template-icon-btn ${tpl.isDefault ? "active" : ""}`}
                    onClick={(e) => handleToggleDefault(tpl.id, tpl.isDefault, e)}
                    title={tpl.isDefault ? "Remove Default" : "Set as Default"}
                  >
                    <svg viewBox="0 0 24 24" width="13" height="13" fill={tpl.isDefault ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5">
                      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                    </svg>
                  </button>
                  <button
                    className="template-icon-btn template-delete-btn"
                    onClick={(e) => handleDelete(tpl.id, e)}
                    title="Delete Template"
                  >
                    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <polyline points="3,6 5,6 21,6" />
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}

          <div className="template-separator" />

          {isSaving ? (
            <div className="template-save-form">
              <input
                ref={saveInputRef}
                type="text"
                className="template-name-input"
                placeholder="Template name..."
                value={saveName}
                maxLength={40}
                onChange={e => setSaveName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") handleSaveTemplate();
                  if (e.key === "Escape") {
                    if (overwriteTarget) { setOverwriteTarget(null); }
                    else { setIsSaving(false); setSaveName(""); }
                  }
                }}
              />

              {overwriteTarget ? (
                <div className="template-overwrite-dialog">
                  <div className="template-overwrite-msg">
                    A template named "<strong>{overwriteTarget.name}</strong>" already exists.
                  </div>
                  <div className="template-overwrite-actions">
                    <button className="template-overwrite-btn" onClick={handleOverwrite}>
                      Overwrite
                    </button>
                    <button className="template-rename-btn" onClick={handleRename}>
                      Rename
                    </button>
                    <button
                      className="template-save-cancel"
                      onClick={() => { setOverwriteTarget(null); setIsSaving(false); setSaveName(""); }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="template-save-actions">
                  <button
                    className="template-save-confirm"
                    onClick={handleSaveTemplate}
                    disabled={!saveName.trim()}
                  >
                    Save
                  </button>
                  <button
                    className="template-save-cancel"
                    onClick={() => { setIsSaving(false); setSaveName(""); }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="template-item template-add-btn" onClick={() => setIsSaving(true)}>
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>Save Current as Template</span>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default TemplateMenu;
